/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import Html from 'Component/Html';
import Image from 'Component/Image';
import Link from 'Component/Link';
import media, {WYSIWYG_MEDIA} from 'Util/Media/Media';

import { getPostUrl } from '../../../../util/Posts';

import './MagazineBlogPostCard.style';

/** @namespace ScandiPWA/Blog/Component/BlogPostCard/MagazineBlogPostCard/Component */
export class MagazineBlogPostCard extends PureComponent {
    static propTypes = {
        post: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.bool
        ]),
        isPlaceholder: PropTypes.bool,
        isCompactOnMobile: PropTypes.bool
    };

    static defaultProps = {
        post: false,
        isPlaceholder: false,
        isCompactOnMobile: false
    };

    monthMap = {
        '01' : __('January'),
        '02' : __('Febrary'),
        '03' : __('March'),
        '04' : __('April'),
        '05' : __('May'),
        '06' : __('June'),
        '07' : __('July'),
        '08' : __('August'),
        '09' : __('September'),
        '10' : __('October'),
        '11' : __('November'),
        '12' : __('December')
    };

    renderImage() {
        const {
            post: {
                first_image,
                media_gallery,
                title
            },
            topFirstMagazine,
            latestMagazine,
            post
        } = this.props;

        if(!topFirstMagazine && latestMagazine && media_gallery) {
            const mediaGalleryArray = media_gallery.split(';');

            if(mediaGalleryArray[0]){
                return (
                    <div
                        mix={ { block: 'MagazineBlogPostCard', elem: 'ImageWrapper' } }
                    >
                        <Image
                            mix={ { block: 'MagazineBlogPostCard', elem: 'Image' } }
                            ratio="square"
                            src={ media(mediaGalleryArray[0]) }
                        />
                    </div>
                );
            }
        }

        if (!first_image) {
            return null;
        }

        return (
            <Link
                to={ getPostUrl(post) }
                title={ __('View %s', title) }
                mix={ { block: 'MagazineBlogPostCard', elem: 'ImageWrapper' } }
            >
                <Image
                    mix={ { block: 'MagazineBlogPostCard', elem: 'Image' } }
                    ratio="square"
                    src={ first_image }
                />
            </Link>
        );
    }

    renderTitle() {
        const { post: { title }, post } = this.props;
        if (!title) {
            return null;
        }

        return (
            <div block="MagazineBlogPostCard" elem="Title">
                <Link to={ getPostUrl(post) }>
                    { title }
                </Link>
            </div>
        );
    }

    renderDescription() {
        const { post: { short_content } } = this.props;

        if (!short_content) {
            return null;
        }

        return <Html content={ short_content } />;
    }
    renderPublishDate() {
        const { post: { publish_time } } = this.props;

        if (!publish_time) {
            return null;
        }

        const dateObj = new Date(publish_time);

        return `${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
    }

    renderContents() {
        const { isPlaceholder, serialNumber, latestMagazine, topFirstMagazine, post, totalPosts } = this.props;

        if (isPlaceholder) {
            return null;
        }

        if(topFirstMagazine && serialNumber === 0) {
            return (
                <>
                    <div block="MagazineBlogPostCard" elem="ContentWrapper" mods={ { expand: true } }>
                        { this.renderImage() }
                        <div block="MagazineBlogPostCard" elem="Details">
                            { this.renderTitle() }
                            <div block="MagazineBlogPostCard" elem="DescriptionWrapper">
                                { this.renderDescription() }
                            </div>
                            <Link to={ getPostUrl(post) } className={'Magazine_LatestPost_link'}>{ __('Αρχείο τευχών') }</Link>
                        </div>
                    </div>
                </>
            );
        }

        if(latestMagazine) {
            return (
                <div block="MagazineBlogPostCard" elem="ContentWrapper" mods={ { expand: true } }>
                    <div block="MagazineBlogPostCard" elem="Details">
                        <div block="MagazineBlogPostCard" elem="DetailsNumber">
                            <p>{ __('ΤΕΥΧΟΣ') }</p>
                            <p>{ this.renderPublishDate() }</p>
                        </div>
                        <div block="MagazineBlogPostCard" elem="DetailsContent">
                            { this.renderTitle() }
                            <div block="MagazineBlogPostCard" elem="DescriptionWrapper">
                                { this.renderDescription() }
                            </div>
                        </div>
                    </div>
                    { this.renderImage() }
                </div>
            );
        }

        return (
            <div block="MagazineBlogPostCard" elem="ContentWrapper" mods={ { expand: true } }>
                { this.renderImage() }
                <div block="MagazineBlogPostCard" elem="Details">
                    <div block="MagazineBlogPostCard" elem="DetailsContent">
                        { this.renderTitle() }
                        <div block="MagazineBlogPostCard" elem="DescriptionWrapper">
                            { this.renderDescription() }
                        </div>
                        <Link to={ getPostUrl(post) } className={'Magazine_LatestPost_link'}>{ __('Ξεφυλλίστε το') }</Link>
                    </div>
                </div>
            </div>
        );
    }


    render() {
        const { isPlaceholder, isCompactOnMobile, serialNumber, topFirstMagazine, post : { publish_time}, normalGrid  } = this.props;
        if(!publish_time) return null;
        let month = publish_time.split('-')[1];

        if(!topFirstMagazine && serialNumber === 0)
            return null;

        if(normalGrid) {
            return (
                <>
                    <div className={"MagazineBlogPostCard-month month-"+month}>{this.monthMap[month]}</div>
                    <div block={`MagazineBlogPostCard month-${month}`} data-monthTile={this.monthMap[month]} mods={ { isLoading: isPlaceholder, isCompactOnMobile } }>
                        { this.renderContents() }
                    </div>
                </>
            );
        }

        return (
            <div block={`MagazineBlogPostCard month-${month}`} data-monthTile={this.monthMap[month]} mods={ { isLoading: isPlaceholder, isCompactOnMobile } }>
                { this.renderContents() }
            </div>
        );
    }
}

export default MagazineBlogPostCard;
